import { fetchAuthSession } from 'aws-amplify/auth';

const API_ENDPOINT = import.meta.env.VITE_API_URL;
if (!API_ENDPOINT) throw new Error('VITE_API_URL not configured');

export interface JVProperty {
  propertyStreet: string;
  propertyCity: string;
  propertyState: string;
  propertyZip: string;
  propertyCounty?: string;
  propertyLine2?: string;
  propertyCountry?: string;
  arv?: string;
  estimatedValue?: string;
  taxSaleAmount?: string;
  taxSaleDate?: string;
  ownerFirstName?: string;
  ownerLastName?: string;
  ownerPhone?: string;
  ownerEmail?: string;
  bedrooms?: string;
  bathrooms?: string;
  sqft?: string;
  propertyCondition?: string;
  repairCost?: string;
  propertyPhotoUrls?: string[];
  maoReportUrls?: string[];
  additionalDocUrls?: string[];
}

export interface JVPropertyResponse extends JVProperty {
  id: string;
  status: string;
  createdAt: string;
  createdBy: string;
}

/**
 * Get authenticated user's ID token for API requests
 */
async function getAuthToken(): Promise<string> {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) {
      throw new Error('No authentication token available');
    }
    return idToken;
  } catch (error) {
    console.error('Error getting auth token:', error);
    throw new Error('Authentication failed');
  }
}

/**
 * Submit a new JV property to the API
 */
export async function submitJVProperty(property: JVProperty): Promise<JVPropertyResponse> {
  const token = await getAuthToken();

  const response = await fetch(`${API_ENDPOINT}/properties`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(property),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to submit property' }));
    throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get all JV properties for the authenticated user
 */
export async function getJVProperties(): Promise<JVPropertyResponse[]> {
  const token = await getAuthToken();

  const response = await fetch(`${API_ENDPOINT}/properties`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch properties' }));
    throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();
  return data.items || [];
}

/**
 * Get a single JV property by ID
 */
export async function getJVProperty(id: string): Promise<JVPropertyResponse> {
  const token = await getAuthToken();

  const response = await fetch(`${API_ENDPOINT}/jv-submissions/${id}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch property' }));
    throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update JV property status
 */
export async function updateJVPropertyStatus(id: string, status: string): Promise<JVPropertyResponse> {
  const token = await getAuthToken();

  const response = await fetch(`${API_ENDPOINT}/jv-submissions/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ status }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to update property' }));
    throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update JV property (full update with all fields)
 */
export async function updateJVProperty(id: string, updates: Partial<JVProperty>): Promise<JVPropertyResponse> {
  const token = await getAuthToken();

  const response = await fetch(`${API_ENDPOINT}/jv-submissions/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to update property' }));
    throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Upload file to S3 using presigned URL
 */
export async function uploadToS3(file: File, folder: string): Promise<string> {
  const token = await getAuthToken();

  // Step 1: Get presigned URL from backend
  const presignedResponse = await fetch(`${API_ENDPOINT}/uploads/presigned-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      fileName: file.name,
      fileType: file.type,
      folder: folder,
    }),
  });

  if (!presignedResponse.ok) {
    throw new Error('Failed to get upload URL');
  }

  const { uploadUrl, fileUrl } = await presignedResponse.json();

  // Step 2: Upload file directly to S3
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload file to S3');
  }

  return fileUrl;
}

export async function getDraft(): Promise<Record<string, unknown> | null> {
  try {
    const token = await getAuthToken();
    const res = await fetch(`${API_ENDPOINT}/jv-drafts`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const body = await res.json();
    return body.draft && Object.keys(body.draft).length > 0 ? body.draft : null;
  } catch {
    return null;
  }
}

export async function saveDraftToBackend(draft: Record<string, unknown>): Promise<void> {
  try {
    const token = await getAuthToken();
    await fetch(`${API_ENDPOINT}/jv-drafts`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ formData: draft }),
    });
  } catch {
    // silent fail — localStorage is the safety net
  }
}

export async function deleteDraft(): Promise<void> {
  try {
    const token = await getAuthToken();
    await fetch(`${API_ENDPOINT}/jv-drafts`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
  } catch {
    // silent fail
  }
}

export async function markDraftSubmitted(): Promise<void> {
  try {
    const token = await getAuthToken();
    await fetch(`${API_ENDPOINT}/jv-drafts`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ submittedAt: new Date().toISOString() }),
    });
  } catch {
    // silent fail — draft record loss is acceptable, submission already succeeded
  }
}
